import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import Button from "@/components/site/Button";
import useSeo from "@/hooks/useSeo";
import { breadcrumbLd } from "@/lib/jsonld";
import { AANBOD_DETAILS } from "@/lib/aanbodDetails";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const DAGEN = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const MAANDEN = [
    "januari", "februari", "maart", "april", "mei", "juni",
    "juli", "augustus", "september", "oktober", "november", "december",
];

function formatDate(iso) {
    const d = new Date(iso);
    return `${DAGEN[(d.getDay() + 6) % 7]} ${d.getDate()} ${MAANDEN[d.getMonth()]}`;
}
function formatDateLong(iso) {
    const d = new Date(iso);
    const dl = ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"];
    return `${dl[d.getDay()]} ${d.getDate()} ${MAANDEN[d.getMonth()]} ${d.getFullYear()}`;
}
function formatTime(iso) {
    const d = new Date(iso);
    return d.toLocaleTimeString("nl-NL", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Amsterdam",
    });
}

const STEPS = ["Sessie", "Moment", "Gegevens", "Bevestigen"];

const EMPTY_FORM = {
    naam: "",
    email: "",
    telefoon: "",
    geboortedatum: "",
    geboortetijd: "",
    geboorteplaats: "",
    vraag: "",
    privacy_akkoord: false,
};

export default function Boeken() {
    useSeo({
        title: "Boek een sessie | Human Design Studio",
        description:
            "Plan je Human Design sessie online. Kies een moment dat bij je past.",
        path: "/boeken",
        jsonLd: [breadcrumbLd("Boeken", "/boeken")],
    });
    const [step, setStep] = useState(1);
    const [aanbod, setAanbod] = useState([]);
    const [selected, setSelected] = useState(null);
    const [slotData, setSlotData] = useState(null);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [slot, setSlot] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState(null);
    const [serverError, setServerError] = useState("");

    useEffect(() => {
        axios
            .get(`${API}/aanbod`)
            .then((r) => setAanbod(r.data.aanbod))
            .catch(() => setAanbod([]));
    }, []);

    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        if (!aanbod.length || selected) return;
        const slug = searchParams.get("sessie");
        if (!slug) return;
        const detail = AANBOD_DETAILS[slug];
        if (!detail?.key) return;
        const match = aanbod.find((a) => a.key === detail.key);
        if (match) {
            chooseSessie(match);
            // keep URL clean
            const next = new URLSearchParams(searchParams);
            next.delete("sessie");
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aanbod]);

    const chooseSessie = (item) => {
        setSelected(item);
        setSlot(null);
        setLoadingSlots(true);
        setStep(2);
        axios
            .get(`${API}/slots?aanbod=${item.key}`)
            .then((r) => setSlotData(r.data))
            .catch(() => setSlotData({ dates: [] }))
            .finally(() => setLoadingSlots(false));
    };

    const validateForm = () => {
        const e = {};
        if (!form.naam.trim()) e.naam = "Vul je naam in.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Vul een geldig e-mailadres in.";
        if (!form.telefoon.trim()) e.telefoon = "Vul je telefoonnummer in.";
        if (!form.geboortedatum.trim()) e.geboortedatum = "Vul je geboortedatum in.";
        if (!form.geboortetijd.trim()) e.geboortetijd = "Vul je geboortetijd in.";
        if (!form.geboorteplaats.trim()) e.geboorteplaats = "Vul je geboorteplaats in.";
        if (!form.privacy_akkoord) e.privacy_akkoord = "Je moet akkoord gaan om verder te gaan.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const goToConfirm = () => {
        if (validateForm()) {
            setServerError("");
            setStep(4);
        }
    };

    const submit = async () => {
        setSubmitting(true);
        setServerError("");
        try {
            const { data } = await axios.post(`${API}/bookings`, {
                aanbod: selected.key,
                starttijd: slot.start,
                ...form,
            });
            if (data.checkout_url) {
                window.location.assign(data.checkout_url);
                return;
            }
            setServerError("Er ging iets mis bij het starten van de betaling.");
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg =
                typeof detail === "string"
                    ? detail
                    : "Er ging iets mis. Probeer het opnieuw.";
            setServerError(msg);
            if (err.response?.status === 409) {
                setStep(2);
                setSlot(null);
                setLoadingSlots(true);
                axios
                    .get(`${API}/slots?aanbod=${selected.key}`)
                    .then((r) => setSlotData(r.data))
                    .finally(() => setLoadingSlots(false));
            }
        } finally {
            setSubmitting(false);
        }
    };

    // ---------------- CONFIRMATION ----------------
    if (confirmation) {
        return (
            <section
                data-testid="boeken-confirmation"
                className="mx-auto max-w-[820px] px-5 md:px-10 pt-28 md:pt-40 pb-32 min-h-[70vh]"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="w-16 h-16 rounded-full bg-[var(--hds-green)] flex items-center justify-center text-[var(--hds-card)] mb-10"
                >
                    <Check size={28} strokeWidth={1.5} />
                </motion.div>
                <h1
                    className="font-[Fraunces] text-[42px] md:text-[64px] leading-[1] tracking-[-0.02em]"
                    style={{ fontWeight: 300 }}
                >
                    Je boeking staat{" "}
                    <em className="italic text-[var(--hds-clay)]" style={{ fontWeight: 300 }}>
                        genoteerd
                    </em>
                </h1>
                <p className="mt-6 text-[17px] leading-[1.6] text-[var(--hds-ink)]/80 max-w-[52ch]">
                    Je ontvangt een bevestiging per e-mail met een betaallink.
                </p>

                <div className="mt-10 rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-8">
                    <dl className="space-y-4 text-[15.5px]">
                        <div className="flex justify-between gap-4 border-b border-[var(--hds-line)] pb-4">
                            <dt className="text-[var(--hds-ink)]/55">Sessie</dt>
                            <dd className="text-right font-medium" data-testid="conf-sessie">
                                {confirmation.aanbod_naam}
                            </dd>
                        </div>
                        <div className="flex justify-between gap-4 border-b border-[var(--hds-line)] pb-4">
                            <dt className="text-[var(--hds-ink)]/55">Wanneer</dt>
                            <dd className="text-right" data-testid="conf-moment">
                                {formatDateLong(confirmation.starttijd)}
                                <br />
                                {formatTime(confirmation.starttijd)} uur (Europe/Amsterdam)
                            </dd>
                        </div>
                        <div className="flex justify-between gap-4 border-b border-[var(--hds-line)] pb-4">
                            <dt className="text-[var(--hds-ink)]/55">Duur</dt>
                            <dd className="text-right">{confirmation.duur} minuten</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                            <dt className="text-[var(--hds-ink)]/55">Bedrag</dt>
                            <dd className="text-right font-medium" data-testid="conf-bedrag">
                                {confirmation.bedrag} euro
                            </dd>
                        </div>
                    </dl>
                </div>

                <div className="mt-10">
                    <Button to="/" variant="secondary" data-testid="conf-home">
                        Terug naar Home
                    </Button>
                </div>
            </section>
        );
    }

    // ---------------- WIZARD ----------------
    return (
        <section className="mx-auto max-w-[980px] px-5 md:px-10 pt-24 md:pt-36 pb-24" data-testid="page-boeken">
            <p className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)]/90 mb-5">
                Boek een sessie
            </p>

            {/* Progress */}
            <div className="flex items-center gap-2 md:gap-4 mb-12" data-testid="boeken-progress">
                {STEPS.map((label, i) => {
                    const n = i + 1;
                    const active = n === step;
                    const done = n < step;
                    return (
                        <div key={label} className="flex items-center gap-2 md:gap-4 flex-1">
                            <div className="flex items-center gap-2 min-w-0">
                                <span
                                    className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[12px] transition-colors duration-400 ${
                                        active
                                            ? "bg-[var(--hds-clay)] text-[var(--hds-card)]"
                                            : done
                                              ? "bg-[var(--hds-green)] text-[var(--hds-card)]"
                                              : "bg-[var(--hds-line)] text-[var(--hds-ink)]/50"
                                    }`}
                                >
                                    {done ? <Check size={13} strokeWidth={2} /> : n}
                                </span>
                                <span
                                    className={`text-[12.5px] tracking-wide hidden sm:inline truncate ${
                                        active ? "text-[var(--hds-ink)]" : "text-[var(--hds-ink)]/45"
                                    }`}
                                >
                                    {label}
                                </span>
                            </div>
                            {i < STEPS.length - 1 && (
                                <span className="flex-1 h-px bg-[var(--hds-line)]" />
                            )}
                        </div>
                    );
                })}
            </div>

            <AnimatePresence mode="wait">
                {/* STEP 1 */}
                {step === 1 && (
                    <motion.div
                        key="step1"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5, ease: EASE }}
                    >
                        <h1 className="font-[Fraunces] text-[38px] md:text-[52px] leading-[1.05] tracking-[-0.02em] mb-3" style={{ fontWeight: 300 }}>
                            Kies je sessie
                        </h1>
                        <p className="text-[14.5px] text-[var(--hds-ink)]/65 mb-8" data-testid="boeken-online-note">
                            Sessies zijn online via Google Meet.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {aanbod.map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => chooseSessie(item)}
                                    data-testid={`sessie-${item.key}`}
                                    className="text-left rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] hover:border-[var(--hds-clay)] p-7 transition-colors duration-400 group"
                                >
                                    <div className="flex items-baseline justify-between gap-3 mb-3">
                                        <h2 className="font-[Fraunces] text-[24px] leading-tight" style={{ fontWeight: 300 }}>
                                            {item.naam}
                                        </h2>
                                        <span className="font-[Fraunces] italic text-[18px] text-[var(--hds-clay)] whitespace-nowrap" style={{ fontWeight: 400 }}>
                                            {item.bedrag} euro
                                        </span>
                                    </div>
                                    <p className="text-[12px] tracking-[0.14em] uppercase text-[var(--hds-ink)]/50 mb-3">
                                        {item.duur} minuten
                                    </p>
                                    <p className="text-[15px] leading-[1.55] text-[var(--hds-ink)]/75">
                                        {item.beschrijving}
                                    </p>
                                    {item.note && (
                                        <p className="mt-3 text-[13px] italic text-[var(--hds-ink)]/55">
                                            {item.note}
                                        </p>
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="mt-8 rounded-[12px] border border-[var(--hds-line)] p-5 text-[14px] text-[var(--hds-ink)]/65">
                            Liever een avondworkshop in een kleine groep?{" "}
                            <Link to="/contact?onderwerp=workshop" className="text-[var(--hds-clay)] underline underline-offset-2">
                                Laat het weten via contact
                            </Link>
                            .
                        </div>
                    </motion.div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                    <motion.div
                        key="step2"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5, ease: EASE }}
                    >
                        <h1 className="font-[Fraunces] text-[38px] md:text-[52px] leading-[1.05] tracking-[-0.02em] mb-3" style={{ fontWeight: 300 }}>
                            Kies een moment
                        </h1>
                        <p className="text-[14px] text-[var(--hds-ink)]/60 mb-8">
                            Alle tijden in Europe/Amsterdam. Sessie: {selected?.naam} ({selected?.duur} min).
                        </p>

                        {serverError && (
                            <div className="mb-6 rounded-[12px] bg-[var(--hds-clay)]/10 border border-[var(--hds-clay)]/30 p-4 text-[14px] text-[var(--hds-clay-dark)]" data-testid="boeken-slot-error">
                                {serverError}
                            </div>
                        )}

                        {loadingSlots ? (
                            <div className="flex items-center gap-3 text-[var(--hds-ink)]/60 py-10">
                                <Loader2 className="animate-spin" size={20} /> Momenten laden...
                            </div>
                        ) : !slotData || slotData.dates.length === 0 ? (
                            <div className="rounded-[14px] border border-[var(--hds-line)] p-8" data-testid="boeken-no-slots">
                                <p className="text-[17px] text-[var(--hds-ink)]/80 mb-5">
                                    Er staan nu geen momenten open. Neem contact op.
                                </p>
                                <Button to="/contact" variant="primary" icon="arrow">
                                    Neem contact op
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {slotData.dates.slice(0, 14).map((day) => (
                                    <div key={day.date}>
                                        <p className="font-[Fraunces] text-[20px] mb-3 capitalize" style={{ fontWeight: 400 }}>
                                            {formatDate(day.date + "T00:00:00")}
                                        </p>
                                        <div className="flex flex-wrap gap-2.5">
                                            {day.slots.map((s) => {
                                                const active = slot?.start === s.start;
                                                return (
                                                    <button
                                                        key={s.start}
                                                        type="button"
                                                        onClick={() => setSlot(s)}
                                                        data-testid={`slot-${s.start}`}
                                                        className={`rounded-full px-5 py-2.5 text-[14px] border transition-colors duration-300 whitespace-nowrap ${
                                                            active
                                                                ? "bg-[var(--hds-clay)] text-[var(--hds-card)] border-[var(--hds-clay)]"
                                                                : "bg-[var(--hds-card)] border-[var(--hds-line)] hover:border-[var(--hds-clay)] text-[var(--hds-ink)]"
                                                        }`}
                                                    >
                                                        {s.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Button variant="secondary" onClick={() => setStep(1)} data-testid="back-1">
                                Terug
                            </Button>
                            {slot && (
                                <Button variant="primary" icon="arrow" onClick={() => setStep(3)} data-testid="next-3">
                                    Verder met {slot.label} uur
                                </Button>
                            )}
                        </div>
                    </motion.div>
                )}

                {/* STEP 3 */}
                {step === 3 && (
                    <motion.div
                        key="step3"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5, ease: EASE }}
                    >
                        <h1 className="font-[Fraunces] text-[38px] md:text-[52px] leading-[1.05] tracking-[-0.02em] mb-8" style={{ fontWeight: 300 }}>
                            Je gegevens
                        </h1>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <Field label="Naam" name="naam" value={form.naam} error={errors.naam} onChange={setForm} form={form} />
                            <Field label="E-mailadres" name="email" type="email" value={form.email} error={errors.email} onChange={setForm} form={form} />
                            <Field label="Telefoon" name="telefoon" value={form.telefoon} error={errors.telefoon} onChange={setForm} form={form} />
                            <Field label="Geboortedatum" name="geboortedatum" type="date" value={form.geboortedatum} error={errors.geboortedatum} onChange={setForm} form={form} />
                            <Field label="Geboortetijd" name="geboortetijd" type="time" value={form.geboortetijd} error={errors.geboortetijd} onChange={setForm} form={form} />
                            <Field label="Geboorteplaats" name="geboorteplaats" value={form.geboorteplaats} error={errors.geboorteplaats} onChange={setForm} form={form} />
                        </div>

                        <div className="mt-5">
                            <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">
                                Je vraag (optioneel)
                            </label>
                            <textarea
                                data-testid="field-vraag"
                                value={form.vraag}
                                onChange={(e) => setForm({ ...form, vraag: e.target.value })}
                                rows={3}
                                className="w-full rounded-[12px] bg-[var(--hds-card)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-3 text-[15px] transition-colors duration-300"
                            />
                        </div>

                        <div className="mt-6 rounded-[12px] bg-[var(--hds-green)]/8 border border-[var(--hds-green)]/20 p-4 text-[13.5px] leading-[1.55] text-[var(--hds-ink)]/70">
                            We vragen je geboortedatum, -tijd en -plaats omdat je bodygraph
                            hierop wordt berekend. Zonder deze gegevens kan Marcel je kaart
                            niet maken.
                        </div>

                        <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="field-privacy-label">
                            <input
                                type="checkbox"
                                data-testid="field-privacy"
                                checked={form.privacy_akkoord}
                                onChange={(e) => setForm({ ...form, privacy_akkoord: e.target.checked })}
                                className="mt-1 w-5 h-5 accent-[var(--hds-clay)] shrink-0"
                            />
                            <span className="text-[14px] leading-[1.55] text-[var(--hds-ink)]/80">
                                Ik ga akkoord met de{" "}
                                <Link to="/privacy" className="text-[var(--hds-clay)] underline underline-offset-2">
                                    privacyverklaring
                                </Link>{" "}
                                en geef toestemming om mijn geboortegegevens te gebruiken voor
                                het maken van mijn bodygraph.
                            </span>
                        </label>
                        {errors.privacy_akkoord && (
                            <p className="mt-2 text-[13px] text-[var(--hds-clay-dark)]" data-testid="error-privacy">
                                {errors.privacy_akkoord}
                            </p>
                        )}

                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Button variant="secondary" onClick={() => setStep(2)} data-testid="back-2">
                                Terug
                            </Button>
                            <Button variant="primary" icon="arrow" onClick={goToConfirm} data-testid="next-4">
                                Naar bevestigen
                            </Button>
                        </div>
                    </motion.div>
                )}

                {/* STEP 4 */}
                {step === 4 && (
                    <motion.div
                        key="step4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5, ease: EASE }}
                    >
                        <h1 className="font-[Fraunces] text-[38px] md:text-[52px] leading-[1.05] tracking-[-0.02em] mb-8" style={{ fontWeight: 300 }}>
                            Bevestigen
                        </h1>

                        <div className="rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-8 space-y-4 text-[15.5px]">
                            <Row label="Sessie" value={`${selected?.naam} (${selected?.duur} min)`} />
                            <Row label="Moment" value={`${formatDateLong(slot.start)}, ${slot.label} uur`} />
                            <Row label="Bedrag" value={`${selected?.bedrag} euro`} />
                            <Row label="Naam" value={form.naam} />
                            <Row label="E-mail" value={form.email} />
                            <Row label="Telefoon" value={form.telefoon} last />
                        </div>

                        {serverError && (
                            <div className="mt-6 rounded-[12px] bg-[var(--hds-clay)]/10 border border-[var(--hds-clay)]/30 p-4 text-[14px] text-[var(--hds-clay-dark)]" data-testid="boeken-submit-error">
                                {serverError}
                            </div>
                        )}

                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Button variant="secondary" onClick={() => setStep(3)} data-testid="back-3">
                                Terug
                            </Button>
                            <Button variant="primary" icon="arrow" onClick={submit} disabled={submitting} data-testid="submit-booking">
                                {submitting ? "Bezig..." : "Naar iDEAL"}
                            </Button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}

const Row = ({ label, value, last }) => (
    <div className={`flex justify-between gap-4 ${last ? "" : "border-b border-[var(--hds-line)] pb-4"}`}>
        <span className="text-[var(--hds-ink)]/55">{label}</span>
        <span className="text-right font-medium">{value}</span>
    </div>
);

const Field = ({ label, name, type = "text", value, error, onChange, form }) => (
    <div>
        <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">
            {label}
        </label>
        <input
            type={type}
            data-testid={`field-${name}`}
            value={value}
            onChange={(e) => onChange({ ...form, [name]: e.target.value })}
            className={`w-full rounded-[12px] bg-[var(--hds-card)] border outline-none px-4 py-3 text-[15px] transition-colors duration-300 ${
                error ? "border-[var(--hds-clay)]" : "border-[var(--hds-line)] focus:border-[var(--hds-clay)]"
            }`}
        />
        {error && (
            <p className="mt-1.5 text-[13px] text-[var(--hds-clay-dark)]" data-testid={`error-${name}`}>
                {error}
            </p>
        )}
    </div>
);
